import { IColumn, IStyle, ITextStyles, ITheme, mergeStyleSets, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPSeparator, IPSpinner, IPSpinnerSize, IPStack, IPText } from 'baseComponents';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { AuditTrailEntryCard } from 'components/Activity/AuditTrailEntryCard';
import GenericModal from 'components/Dialogs/GenericModal';
import BaseTableList, { IBaseTableList } from 'components/TableList/BaseTableList';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import {
  globalStackTokensGapLarge,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { TFunction } from 'i18next';
import KPI, { KPITypes } from 'models/kpi/kpi';
import { KPIDefinitionRevisionInfo } from 'models/kpi/kpiDefinitionRevision';
import { FormVersionEventActor } from 'models/tasks/formVersion';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetKPIDefinitionRevisions } from 'services/Api/kpiService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTimeNoSeconds } from 'utils/datetime';
import { getHtmlJSX } from 'utils/html';
import { sortOnDate, sortOnNumber } from 'utils/sorting';

interface IKPIDefinitionHistoryModalProps {
  kpi: KPI;
  isOpen: boolean;
  onDismiss: () => void;
}

// One property of the definition as the detail pane reads it. The code is the diff vocabulary the form
// history uses, so a row knows whether the selected version is what changed it.
interface IDefinitionProperty {
  code: string;
  label: string;
  value: string;
}

// The badge and hairline-row shapes the form history modal draws its version snapshot with: both panes
// answer "what did this version change?", so they read as one pattern. The label keeps messageText -
// Fluent's colour for text ON these backgrounds - because warningText is not inverted for dark mode
// and would vanish into the tint there.
const changedBadgeBase: IStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 600,
  borderRadius: 10,
  padding: '2px 8px',
  whiteSpace: 'nowrap',
};

const getStyles = (theme: ITheme) =>
  mergeStyleSets({
    valueHeader: {
      borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
      paddingBottom: 4,
    },
    valueRow: {
      borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
      padding: '6px 0',
    },
    changedBadge: [
      changedBadgeBase,
      { color: theme.semanticColors.messageText, backgroundColor: theme.semanticColors.warningBackground },
    ],
    timeline: {
      marginTop: 16,
      paddingTop: 12,
    },
  });

// Choices are a LIST, so the diff compares the ordered snapshot (key, value, description) rather
// than a per-property pair, mirroring the server's choice signature in the form-version diff.
const getChoiceSignature = (revision: KPIDefinitionRevisionInfo): string =>
  JSON.stringify(
    [...revision.choices]
      .sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder) || sortOnNumber(a.choiceKey, b.choiceKey))
      .map((choice) => [choice.choiceKey, choice.value ?? '', choice.description ?? '']),
  );

const getChoicesText = (revision: KPIDefinitionRevisionInfo): string | undefined =>
  revision.choices.length > 0
    ? [...revision.choices]
        .sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder) || sortOnNumber(a.choiceKey, b.choiceKey))
        .map((choice) => choice.value || String(choice.choiceKey))
        .join(', ')
    : undefined;

const KPIDefinitionHistoryModal = (props: IKPIDefinitionHistoryModalProps) => {
  const { t } = useTranslation(['forms', 'kpis', 'translation']);
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const styles = getStyles(theme);
  const [revisions, setRevisions] = useState<KPIDefinitionRevisionInfo[]>([]);
  const [selectedRevision, setSelectedRevision] = useState<KPIDefinitionRevisionInfo | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const listRef = useRef<IBaseTableList<KPIDefinitionRevisionInfo> | null>(null);

  const getColumnsList = (): IColumn[] => [
    {
      key: 'version',
      name: t('forms:Versioning.Columns.Version'),
      minWidth: 50,
      maxWidth: 60,
      isResizable: false,
    },
    {
      // The one column with something to read - the name the definition carried in that version - so
      // it takes what the fixed columns leave and is the only one without a maxWidth.
      key: 'name',
      name: t('forms:Versioning.Columns.Name'),
      minWidth: 140,
      isRowHeader: true,
      isResizable: true,
    },
    {
      // Wide enough for the date AND the time on one line: two versions a reader has to tell apart
      // are often minutes, not days, apart.
      key: 'date',
      name: t('forms:Versioning.Columns.Changed'),
      minWidth: 120,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'changedBy',
      name: t('forms:Versioning.Columns.ChangedBy'),
      minWidth: 120,
      maxWidth: 160,
      isResizable: true,
    },
    {
      // How MUCH this version touched; which properties those were is named in the pane.
      key: 'changes',
      name: t('forms:Versioning.Columns.Changes'),
      minWidth: 60,
      maxWidth: 70,
      isResizable: false,
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(getColumnsList());

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    const loadRevisions = async () => {
      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const items = await apiGetKPIDefinitionRevisions(props.kpi.kpiId, accessToken);
        setRevisions(items);
        setSelectedRevision(items.find((item) => item.isCurrent) ?? items[0]);
      } catch (error) {
        appContext.setError(error);
      } finally {
        setIsLoading(false);
      }
    };
    void loadRevisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.kpi.kpiId]);

  // Bring the version whose detail is on the right into view - on open that is the current one, which
  // on a long history is not the row the grid happens to start on. Selecting it too keeps the grid's
  // checked row in step when the selection comes from code rather than a click. isLoading is a
  // dependency because the loader unmounts the grid: revisions and selection are set while it still
  // shows, so the sync has to run again on the render that mounts the grid.
  useEffect(() => {
    if (isLoading || !selectedRevision) return;
    const index = revisions.findIndex(
      (item) => item.kpiDefinitionRevisionId === selectedRevision.kpiDefinitionRevisionId,
    );
    if (index < 0) return;

    listRef.current?.scroll(index, true, revisions);
  }, [revisions, selectedRevision, isLoading]);

  const getActorText = (revision: KPIDefinitionRevisionInfo): string => {
    switch (revision.source) {
      case FormVersionEventActor.Human:
        return revision.actorId
          ? appContext.globalDataCache.users.get(revision.actorId)?.name || t('forms:Versioning.Timeline.UnknownUser')
          : t('forms:Versioning.Timeline.UnknownUser');
      case FormVersionEventActor.Projector:
        return t('forms:Versioning.Timeline.Projector');
      case FormVersionEventActor.Copy:
        return t('forms:Versioning.Timeline.Copy');
      case FormVersionEventActor.Migration:
        return t('forms:Versioning.Timeline.Migration');
      default:
        return t('forms:Versioning.System');
    }
  };

  // The version this one replaced: the highest revision number below it rather than revision - 1, so
  // a gap in the numbering cannot leave a version comparing against nothing.
  const getPreviousRevision = (revision: KPIDefinitionRevisionInfo): KPIDefinitionRevisionInfo | undefined =>
    revisions.reduce<KPIDefinitionRevisionInfo | undefined>(
      (previous, candidate) =>
        candidate.revision < revision.revision && (!previous || candidate.revision > previous.revision)
          ? candidate
          : previous,
      undefined,
    );

  // The definition texts are language-resolved server-side, so a translation-only edit in another
  // language can legitimately yield an empty list here.
  const getChangedCodes = (revision: KPIDefinitionRevisionInfo): string[] => {
    const previous = getPreviousRevision(revision);
    if (!previous) {
      return [];
    }

    const changes: string[] = [];
    if (revision.name !== previous.name) changes.push('Name');
    if (revision.description !== previous.description) changes.push('Description');
    if (revision.instruction !== previous.instruction) changes.push('Instruction');
    if (revision.expectedResult !== previous.expectedResult) changes.push('ExpectedResult');
    if (revision.type !== previous.type) changes.push('Type');
    if (revision.required !== previous.required) changes.push('Required');
    if (revision.minValue !== previous.minValue) changes.push('Minimum');
    if (revision.maxValue !== previous.maxValue) changes.push('Maximum');
    if (revision.decimalCount !== previous.decimalCount) changes.push('DecimalPlaces');
    if (revision.defValue !== previous.defValue) changes.push('DefaultValue');
    if (revision.showLabel !== previous.showLabel) changes.push('ShowLabel');
    if (revision.attachmentMode !== previous.attachmentMode) changes.push('AttachmentRequirement');
    if (revision.commentMode !== previous.commentMode) changes.push('CommentRequirement');
    if (getChoiceSignature(revision) !== getChoiceSignature(previous)) changes.push('Choices');

    return changes;
  };

  const getPropertyLabel = (code: string): string => {
    switch (code) {
      case 'Name':
        return t('forms:Versioning.Diff.Name');
      case 'Description':
        return t('forms:Versioning.Diff.Description');
      case 'Instruction':
        return t('forms:Versioning.Diff.Instruction');
      case 'ExpectedResult':
        return t('forms:Versioning.Diff.ExpectedResult');
      case 'Type':
        return t('forms:Versioning.Diff.Type');
      case 'Required':
        return t('forms:Versioning.Diff.Required');
      case 'Minimum':
        return t('forms:Versioning.Diff.Minimum');
      case 'Maximum':
        return t('forms:Versioning.Diff.Maximum');
      case 'DecimalPlaces':
        return t('forms:Versioning.Diff.DecimalPlaces');
      case 'DefaultValue':
        return t('forms:Versioning.Diff.DefaultValue');
      case 'ShowLabel':
        return t('forms:Versioning.Diff.ShowLabel');
      case 'AttachmentRequirement':
        return t('forms:Versioning.Diff.AttachmentRequirement');
      case 'CommentRequirement':
        return t('forms:Versioning.Diff.CommentRequirement');
      case 'Choices':
        return t('forms:Versioning.Diff.Choices');
      default:
        return code;
    }
  };

  const getBooleanText = (value: boolean): string =>
    value ? t('translation:General.Words.Yes') : t('translation:General.Words.No');

  // The type name lives on the KPI model and a revision carries the type number alone, so it is read
  // off a clone of the KPI being edited - which also keeps a system KPI reporting as one.
  const getTypeText = (revision: KPIDefinitionRevisionInfo): string => {
    const typed = props.kpi.clone();
    typed.type = revision.type;

    return typed.getTypeText(t as TFunction<string[]>);
  };

  // Attachments and comments are one value in the data (sign = requirement, magnitude = how many or
  // how long) and two controls in the editor, so they are read back the same way round.
  const getRequirementText = (mode: number | undefined): string => {
    const requirement = Math.sign(mode ?? 0);
    if (requirement === 0) {
      return t('kpis:RequiredOptional.None');
    }

    const label = requirement > 0 ? t('kpis:RequiredOptional.Required') : t('kpis:RequiredOptional.Optional');

    return `${label} (${Math.abs(mode ?? 0)})`;
  };

  // Every property that has something to say in this version, plus any the version emptied - a cleared
  // instruction is exactly the kind of change the reader opened this for. The ones that always mean
  // something (name, type, required, show label) are always listed.
  const getProperties = (revision: KPIDefinitionRevisionInfo, changedCodes: string[]): IDefinitionProperty[] => {
    const rows: IDefinitionProperty[] = [];
    const add = (code: string, value: string | undefined, always = false) => {
      if (!value && !always && !changedCodes.includes(code)) {
        return;
      }

      rows.push({ code, label: getPropertyLabel(code), value: value || '-' });
    };

    add('Name', revision.name, true);
    add('Type', getTypeText(revision), true);
    add('Choices', getChoicesText(revision));
    add('Description', revision.description);
    add('Instruction', revision.instruction);
    add('ExpectedResult', revision.expectedResult);
    add('Required', getBooleanText(revision.required), true);
    add('ShowLabel', getBooleanText(revision.showLabel), true);
    add('DefaultValue', revision.defValue);
    add('Minimum', revision.minValue);
    add('Maximum', revision.maxValue);
    add('DecimalPlaces', revision.decimalCount ? String(revision.decimalCount) : undefined);
    add('AttachmentRequirement', revision.attachmentMode ? getRequirementText(revision.attachmentMode) : undefined);
    add('CommentRequirement', revision.commentMode ? getRequirementText(revision.commentMode) : undefined);

    return rows;
  };

  // The grid's own selection also fires when the list preselects the current version after a load, so
  // ignore a selection that changes nothing.
  const selectRevision = (revision: KPIDefinitionRevisionInfo) => {
    if (revision.kpiDefinitionRevisionId === selectedRevision?.kpiDefinitionRevisionId) {
      return;
    }

    setSelectedRevision(revision);
  };

  const valueHeaderStyles: ITextStyles = {
    root: { color: theme.semanticColors.bodySubtext, fontWeight: 600 },
  };

  const renderRevisionColumn = (
    revision: KPIDefinitionRevisionInfo | undefined,
    column: IColumn,
  ): JSX.Element | null => {
    if (!revision || !column) return null;

    switch (column.key) {
      case 'version':
        return <IPText>{revision.revision}</IPText>;
      case 'name':
        return <IPText>{revision.name}</IPText>;
      case 'date':
        return <IPText>{toLocaleDateTimeNoSeconds(revision.created)}</IPText>;
      case 'changedBy':
        return <IPText>{getActorText(revision)}</IPText>;
      case 'changes':
        return <IPText>{getPreviousRevision(revision) ? getChangedCodes(revision).length : ''}</IPText>;
      default:
        return null;
    }
  };

  const renderProperty = (
    property: IDefinitionProperty,
    revision: KPIDefinitionRevisionInfo,
    changedCodes: string[],
  ): JSX.Element => (
    <IPStack
      key={property.code}
      horizontal
      horizontalAlign="space-between"
      verticalAlign="center"
      className={styles.valueRow}
      tokens={globalStackTokensGapSmall}
    >
      <IPStack.Item shrink styles={{ root: { minWidth: 0 } }}>
        <IPText>{property.label}</IPText>
      </IPStack.Item>
      <IPStack.Item shrink styles={{ root: { minWidth: 0 } }}>
        <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
          <IPStack.Item shrink styles={{ root: { minWidth: 0 } }}>
            {property.code === 'DefaultValue' &&
            (revision.type === KPITypes.Html || revision.type === KPITypes.SuccesError) ? (
              <div>{getHtmlJSX(property.value, true, true) ?? '-'}</div>
            ) : (
              <IPText>{property.value}</IPText>
            )}
          </IPStack.Item>
          {changedCodes.includes(property.code) && (
            <IPStack.Item shrink={false}>
              <IPText className={styles.changedBadge}>{t('forms:Versioning.Diff.Changed')}</IPText>
            </IPStack.Item>
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  const renderDetail = (revision: KPIDefinitionRevisionInfo): JSX.Element => {
    const previous = getPreviousRevision(revision);
    const changedCodes = getChangedCodes(revision);
    const properties = getProperties(revision, changedCodes);

    return (
      <>
        {/* The section headings are body text in bold, like every other heading in the app: anything
            larger reads as a second modal title. */}
        <IPText block styles={globalTextStylesBold}>
          {t('forms:Versioning.KPIHistoryValues')}
        </IPText>
        <IPText variant="small" block styles={{ root: { paddingTop: 2 } }}>
          {previous
            ? t('forms:Versioning.KPIHistoryValuesDescription', { version: previous.revision })
            : t('forms:Versioning.KPIHistoryValuesFirst')}
        </IPText>
        <IPStack styles={{ root: { marginTop: 12 } }}>
          {/* Says in two words what the rows below are, so the list is never read as loose lines of
              text under a heading. */}
          <IPStack
            horizontal
            horizontalAlign="space-between"
            verticalAlign="center"
            className={styles.valueHeader}
            tokens={globalStackTokensGapSmall}
          >
            <IPText variant="small" styles={valueHeaderStyles}>
              {t('forms:Versioning.KPIHistoryProperty')}
            </IPText>
            <IPText variant="small" styles={valueHeaderStyles}>
              {t('forms:Versioning.KPIHistoryValue')}
            </IPText>
          </IPStack>
          {properties.map((property) => renderProperty(property, revision, changedCodes))}
        </IPStack>
        {/* A definition version IS a change log entry, so it is drawn by the change log's own card -
            the one the audit trail and the form history render with - instead of a lookalike that
            would drift away from them. */}
        <IPStack className={styles.timeline} tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <IPText block styles={globalTextStylesBold}>
              {t('forms:Versioning.Timeline.Title')}
            </IPText>
          </IPStack.Item>
          <IPStack.Item>
            <AuditTrailEntryCard
              userId={revision.source === FormVersionEventActor.Human ? revision.actorId : undefined}
              userName={getActorText(revision)}
              date={revision.created}
              text={t('forms:Versioning.KPIHistoryCreated', { version: revision.revision })}
              detail={changedCodes.length > 0 ? changedCodes.map(getPropertyLabel).join(', ') : undefined}
            />
          </IPStack.Item>
        </IPStack>
      </>
    );
  };

  return (
    <GenericModal
      title={t('forms:Versioning.KPIHistoryTitle')}
      subTitle={t('forms:Versioning.KPIHistorySubTitle')}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      isBlocking={true}
      width={'min(94vw, 1200px)'}
      maxWidth={'1200px'}
      minWidth={300}
      height={'min(86vh, 700px)'}
      maxHeight={'86vh'}
      titleSeparator
      verticalGap={globalStackTokensGapMedium}
    >
      {isLoading ? (
        <IPStack horizontalAlign="center" styles={{ root: { padding: 20 } }}>
          <IPSpinner size={IPSpinnerSize.medium} />
        </IPStack>
      ) : revisions.length === 0 ? (
        <IPText variant="small">{t('forms:Versioning.Timeline.Empty')}</IPText>
      ) : (
        <IPStack
          horizontal={!appContext.isMobileView}
          tokens={globalStackTokensGapLarge}
          styles={{ root: { height: '100%' } }}
        >
          <IPStack.Item
            styles={{
              root: {
                // The grid needs every one of its five columns at once - below this share the last
                // ones fall off the right edge and the list grows a horizontal scrollbar.
                flex: appContext.isMobileView ? undefined : '0 0 56%',
                minWidth: 0,
                height: appContext.isMobileView ? 300 : '100%',
              },
            }}
          >
            <IPStack
              verticalFill
              styles={{
                root: {
                  // BaseTableList draws inside an IPScrollablePane, which needs a positioned parent
                  // with a real height to bound its own scrolling.
                  position: 'relative',
                  height: '100%',
                  minHeight: 0,
                },
              }}
            >
              <BaseTableList
                ref={listRef}
                items={revisions}
                columns={columns}
                // The pane on the right describes the checked row, so the grid carries a real single
                // selection instead of a bare row click.
                selectionMode={IPSelectionMode.single}
                isLoading={false}
                compact
                noTopPadding
                getKey={(revision) => String(revision.kpiDefinitionRevisionId)}
                setColumns={setColumns}
                onRenderCol={renderRevisionColumn}
                // Only the two axes that mean something here: every row carries the same definition,
                // and the actor and the number of changes are facts about a version, not an order to
                // read it in.
                isSortEnabled={(column) => column.key === 'version' || column.key === 'date'}
                compare={(a, b, columnKey) =>
                  columnKey === 'date' ? sortOnDate(a.created, b.created) : sortOnNumber(a.revision, b.revision)
                }
                // An empty selection is transient (item swaps during reload), never a user intent, so
                // the detail pane keeps showing the last selected version.
                onSelect={(items) => {
                  if (items.length > 0) selectRevision(items[0]);
                }}
              />
            </IPStack>
          </IPStack.Item>
          {/* The two halves are a list and the detail of what is selected in it, so they get the same
              divider the forms scene draws between its rail and its pane. Only side by side: stacked
              on mobile a vertical rule has no height to draw. */}
          {!appContext.isMobileView && <IPSeparator vertical />}
          <IPStack.Item
            grow
            styles={{
              root: {
                minWidth: 0,
                minHeight: 0,
                height: appContext.isMobileView ? undefined : '100%',
                paddingTop: appContext.isMobileView ? 12 : 0,
              },
            }}
          >
            {/* The grid on the left brought its own scroll pane, so the detail scrolls on its own too -
                otherwise a long value list would push the whole modal body instead. */}
            <IPStack verticalFill styles={{ root: { height: '100%', minHeight: 0 } }}>
              <ScrollableStackItem minHeight={100} disableScroll={appContext.isMobileView}>
                {/* The rows in here are right-aligned against the pane's own scrollbar, so the content
                    keeps a gutter away from it instead of touching it. */}
                <IPStack styles={{ root: { paddingRight: 10 } }}>
                  {selectedRevision && renderDetail(selectedRevision)}
                </IPStack>
              </ScrollableStackItem>
            </IPStack>
          </IPStack.Item>
        </IPStack>
      )}
    </GenericModal>
  );
};

export default KPIDefinitionHistoryModal;
